import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { addMonths, format } from 'date-fns';
// @ts-ignore
import { saveAs } from 'file-saver';
// @ts-ignore
import he from 'he';
import {
  combineLatest,
  firstValueFrom,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import { filter, map, shareReplay, tap } from 'rxjs/operators';
import { Users } from 'server/shared/collections/users';
import { addTimeZoneOffset } from 'server/shared/helpers/removeTimeZoneOffset';
import { Certificate } from 'server/shared/models/certificate';
import { CustomerTypeEnum } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { PoolerType } from 'server/shared/models/poolerType';
import { ReportsEnum } from 'server/shared/models/reports.enum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { User } from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { queryParam$ } from 'src/helpers/queryParam$';
import { CertificateService } from '../services/certificate.service';
import { CustomerService } from '../services/customer.service';
import { PermissionsService } from '../services/permissions.service';
import { ToastService } from '../services/toast/toast.service';
import { TranslationService } from '../services/translation.service';
import { UserService } from '../services/user.service';

@Component({
  selector: 'reports-index',
  templateUrl: 'reports-index.html',
  styleUrls: ['reports-index.scss'],
})
export class ReportsIndexComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();
  i18n = this.translationService.getTranslations('reports');
  isLoading: boolean = false;
  loggedInUser?: User;
  displayTimeTrackingReport = false;
  displayTransferReport = false;
  displayJobTypeFilter = true;
  selectedJobType?: JobTypeEnum;
  jobTypeEnum = JobTypeEnum;
  reportsEnum = ReportsEnum;
  displayPoolerTypeFilter = false;
  poolerTypes: PoolerType[] = [];

  // Query param observables
  fromParam$ = queryParam$(this.route, 'from');

  fromDate$: Observable<Date | null> = this.fromParam$.pipe(
    map((from) => (from ? new Date(from) : null)),
    shareReplay(1),
  );

  toParam$ = queryParam$(this.route, 'to');

  toDate$: Observable<Date | null> = this.toParam$.pipe(
    map((to) => (to ? new Date(to) : null)),
    shareReplay(1),
  );

  departmentParam$ = queryParam$(this.route, 'departments');
  selectedDepartmentIds$: Observable<string[]> = this.departmentParam$.pipe(
    map((departments) => {
      if (departments) {
        return departments.split(',').filter((id: string) => id);
      }
      return [];
    }),
    shareReplay(1),
  );

  certificateParam$ = queryParam$(this.route, 'certificates');
  selectedCertificateIds$: Observable<string[]> = this.certificateParam$.pipe(
    map((certificates) => {
      if (certificates) {
        return certificates.split(',').filter((id: string) => id);
      }
      return [];
    }),
    shareReplay(1),
  );

  selectedCertificates$: Observable<Certificate[]> = combineLatest([
    this.selectedCertificateIds$,
    this.certificateService.readyCertificates$,
  ]).pipe(
    map(([certificateIds, certificates]) => {
      if (!certificates || certificateIds.length === 0) {
        return [];
      }
      return certificates.filter((cert) => certificateIds.includes(cert._id));
    }),
    shareReplay(1),
  );

  poolerTypeParam$ = queryParam$(this.route, 'poolerType');

  customersParam$ = queryParam$(this.route, 'customers');
  selectedCustomerIds$: Observable<string[]> = this.customersParam$.pipe(
    map((customers) => {
      if (customers) {
        return customers.split(',').filter((id: string) => id);
      }
      return [];
    }),
    shareReplay(1),
  );

  displayCustomerFilter$ = combineLatest([
    this.customerService.readyDispoCustomer$,
    this.customerService.sharedPoolCustomers$,
  ]).pipe(
    map(([customer, sharedPool]) => {
      const hasTransfers =
        !!customer?.customerTransfers?.transfers?.length ||
        !!customer?.customerTransfers?.employmentTransfers?.length;
      const hasSharedPool = (sharedPool?.length ?? 0) > 0;
      return hasTransfers || hasSharedPool;
    }),
    shareReplay(1),
  );

  selectedPoolerType$: Observable<PoolerType | null> = combineLatest([
    this.poolerTypeParam$,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(([poolerTypeId, customer]) => {
      if (!poolerTypeId || !customer?.poolerTypes) {
        return null;
      }
      return customer.poolerTypes.find((pt) => pt.id === poolerTypeId) || null;
    }),
    shareReplay(1),
  );

  hasValidDateRange$: Observable<boolean> = combineLatest([
    this.fromParam$,
    this.toParam$,
  ]).pipe(
    map(([from, to]) => !!(from && to && to >= from)),
    shareReplay(1),
  );

  currentUser$ = this.userService.readyDispoOrRequester$;
  currentCustomer$ = this.customerService.readyDispoCustomer$;
  hiddenReportIds$ = this.currentCustomer$.pipe(
    map((customer) => customer?.hiddenReports ?? []),
    shareReplay(1),
  );
  isHospitalCustomer$ = this.customerService.readyDispoCustomer$.pipe(
    map((customer) => customer?.customerType === CustomerTypeEnum.HOSPITAL),
  );

  canAccessReports$ = this.permissionsService.canAccessReports$;

  redirectIfNoAccessEffect$ = this.canAccessReports$.pipe(
    filter((canAccess) => !canAccess),
    takeUntil(this.destroyed$),
    tap(() => this.router.navigate(['/dashboard'])),
  );

  // Display flags for each report - DISPO only unless specified
  displayJobsForInvoiceReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO && user?.isAdmin),
  );
  displayCurrentlyActiveEmploymentsReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayExternalCancellationsReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayActivityReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayShiftsPlanningReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayJobOverviewReport$ = combineLatest([
    this.currentUser$,
    this.currentCustomer$,
  ]).pipe(
    map(([user, customer]) => {
      if (user?.role === RoleEnum.DISPO) {
        return true;
      }
      if (user?.role === RoleEnum.REQUESTER) {
        return (
          (customer?.requestersCanAccessReports &&
            customer?.requestersCanAccessJobOverviewReport) ||
          false
        );
      }
      return false;
    }),
  );
  displayDisponentOverviewReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayRequesterOverviewReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayPoolerOverviewReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayAvailableUserJobPlanningReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayPoolerReactionTimeReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displaySmsUsageReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayUserAvailabilityReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayArchivedJobsReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayAvailabilityUpdatesReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayPlanningMatrixReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayHoursByDepartmentReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );
  displayCustomerConnectionsReport$ = this.currentUser$.pipe(
    map((user) => user?.role === RoleEnum.DISPO),
  );

  dynamicMinFromDate?: Date;
  dynamicMaxToDate?: Date;

  private userTracker?: Tracker.Computation;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private ngZone: NgZone,
    private translationService: TranslationService,
    private certificateService: CertificateService,
    private customerService: CustomerService,
    private userService: UserService,
    private permissionsService: PermissionsService,
  ) {
    this.redirectIfNoAccessEffect$.subscribe();
  }

  ngOnInit(): void {
    this.userTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        this.loggedInUser = Users.findOne(Meteor.userId());
      });
    });
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.ngZone.run(() => {
          this.displayTimeTrackingReport =
            customer?.timeTrackingEnabled || false;
          this.displayTransferReport =
            !!customer?.customerTransfers?.transfers?.length || false;
          this.displayJobTypeFilter = !customer?.hiddenFields?.jobtype;
          this.displayPoolerTypeFilter = customer.enablePoolerTypes || false;
          this.poolerTypes = customer.poolerTypes || [];
        });
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.userTracker) {
      this.userTracker.stop();
    }
  }

  private updateQueryParams(newQueryParams: Params): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: newQueryParams,
      queryParamsHandling: 'merge',
    });
  }

  async getFiltersFromQuery(): Promise<{
    from?: string;
    to?: string;
    selectedDepartmentIds: string[];
    selectedCertificates: Certificate[];
    selectedPoolerType?: PoolerType | null;
    selectedCustomerIds: string[];
  }> {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
      selectedCustomerIds,
    } = await firstValueFrom(
      combineLatest({
        from: this.fromParam$,
        to: this.toParam$,
        selectedDepartmentIds: this.selectedDepartmentIds$,
        selectedCertificates: this.selectedCertificates$,
        selectedPoolerType: this.selectedPoolerType$,
        selectedCustomerIds: this.selectedCustomerIds$,
      }),
    );

    return {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
      selectedCustomerIds,
    };
  }

  get userRole(): RoleEnum {
    return Session.get('role');
  }

  selectFromDate(event: MatDatepickerInputEvent<any & string>) {
    this.dynamicMaxToDate = addMonths(new Date(event.value), 3);
    if (this.loggedInUser?.role === RoleEnum.DISPO && this.loggedInUser.isAdmin)
      this.dynamicMaxToDate = undefined;
    const fromValue = format(event.value, dbDayDateToken);
    this.updateQueryParams({ from: fromValue });
  }

  selectToDate(event: MatDatepickerInputEvent<any & string>) {
    this.dynamicMinFromDate = addMonths(new Date(event.value), -3);
    if (this.loggedInUser?.role === RoleEnum.DISPO && this.loggedInUser.isAdmin)
      this.dynamicMinFromDate = undefined;
    const toValue = format(event.value, dbDayDateToken);
    this.updateQueryParams({ to: toValue });
  }

  async selectDepartments(departments?: Department[]) {
    const departmentIds = departments
      ? departments.map((department) => department.id)
      : [];
    this.updateQueryParams({
      departments: departmentIds.length > 0 ? departmentIds.join(',') : null,
    });
  }

  async selectCertificates(certificates?: Certificate[]) {
    const certificateIds = certificates
      ? certificates.map((cert) => cert._id)
      : [];
    this.updateQueryParams({
      certificates: certificateIds.length > 0 ? certificateIds.join(',') : null,
    });
  }

  selectPoolerType(poolerTypeId: string | undefined) {
    this.updateQueryParams({ poolerType: poolerTypeId || null });
  }

  async downloadTimeTrackingReport(type: 'EXCEL' | 'CSV' = 'CSV') {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
      selectedCustomerIds,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getTimeTrackingReport',
      from,
      to,
      'all',
      type,
      selectedCertificates || [],
      this.i18n,
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      selectedCustomerIds.length > 0 ? selectedCustomerIds : null,
      (error: Meteor.Error, buffer: ArrayBuffer) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        const fileName = `timeTracking_${this.getTimespanFilenameString(
          from,
          to,
        )}`;
        if (type === 'EXCEL') {
          this.saveFile(
            fileName,
            '.xlsx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            buffer,
          );
        } else {
          this.saveFile(fileName, '.csv', 'text/csv', buffer);
        }
      },
    );
  }

  async downloadTimeTrackingReportPerPooler(type: 'EXCEL' | 'CSV' = 'EXCEL') {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
      selectedCustomerIds,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getTimeTrackingReport',
      from,
      to,
      'pooler',
      type,
      selectedCertificates || [],
      this.i18n,
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      selectedCustomerIds.length > 0 ? selectedCustomerIds : null,
      async (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.saveFile(
          'poolerTimeTracking_' + this.getTimespanFilenameString(from, to),
          '.zip',
          'application/x-zip-compressed',
          data,
        );
      },
    );
  }

  async downloadTimeTrackingReportPerOrganisation(
    type: 'EXCEL' | 'CSV' = 'EXCEL',
  ) {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
      selectedCustomerIds,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getTimeTrackingReport',
      from,
      to,
      'organisation',
      type,
      selectedCertificates || [],
      this.i18n,
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      selectedCustomerIds.length > 0 ? selectedCustomerIds : null,
      async (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.saveFile(
          'organisationTimeTracking_' +
            this.getTimespanFilenameString(from, to),
          '.zip',
          'application/x-zip-compressed',
          data,
        );
      },
    );
  }

  async downloadTimeTrackingReportPerCustomer(type: 'EXCEL' | 'CSV' = 'EXCEL') {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
      selectedCustomerIds,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getTimeTrackingReport',
      from,
      to,
      'perCustomer',
      type,
      selectedCertificates || [],
      this.i18n,
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      selectedCustomerIds.length > 0 ? selectedCustomerIds : null,
      async (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.saveFile(
          'customerTimeTracking_' + this.getTimespanFilenameString(from, to),
          '.zip',
          'application/x-zip-compressed',
          data,
        );
      },
    );
  }

  async downloadShiftPlanningReport() {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
      selectedCustomerIds,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getShiftPlanningReport',
      from,
      to,
      this.i18n,
      false,
      selectedCertificates || [],
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      selectedCustomerIds.length > 0 ? selectedCustomerIds : null,
      (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.ngZone.run(() => {
            this.isLoading = false;
          });
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.displayCSV(data, 'shiftPlanningReport.csv');
      },
    );
  }

  async downloadJobOverviewReport() {
    const { from, to, selectedDepartmentIds, selectedCertificates } =
      await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getJobOverviewReport',
      from,
      to,
      this.i18n,
      selectedCertificates || [],
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      (error: Meteor.Error, buffer: ArrayBuffer) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.saveFile(
          'jobOverviewReport',
          '.xlsx',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          buffer,
        );
      },
    );
  }
  async downloadJobsForInvoiceReport() {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getJobsForInvoiceReport',
      from,
      to,
      selectedCertificates || [],
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.displayCSV(data, 'shiftPlanningReport.csv');
      },
    );
  }

  async downloadCurrentlyActiveEmploymentsReport() {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedCustomerIds,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getCurrentlyActiveEmploymentsReport',
      from,
      to,
      this.i18n,
      selectedCertificates || [],
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      selectedCustomerIds.length > 0 ? selectedCustomerIds : null,
      (error: Meteor.Error, buffer: ArrayBuffer) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        const fileName = `currentlyActiveEmploymentsReport_${format(
          new Date(from!),
          'd.M.y',
        )}-${format(new Date(to!), 'd.M.y')}`;

        this.saveFile(
          fileName,
          '.xlsx',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          buffer,
        );
      },
    );
  }

  async downloadShiftPlanningReportPerPooler() {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
      selectedCustomerIds,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getShiftPlanningReport',
      from,
      to,
      this.i18n,
      true,
      selectedCertificates || [],
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      selectedCustomerIds.length > 0 ? selectedCustomerIds : null,
      (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.ngZone.run(() => {
            this.isLoading = false;
          });
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.saveFile(
          'shiftPlanningReport_' + this.getTimespanFilenameString(from, to),
          '.zip',
          'application/x-zip-compressed',
          data,
        );
      },
    );
  }

  async downloadAvailabilityInactivityReport() {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getActivityReport',
      from,
      to,
      selectedCertificates || [],
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.displayCSV(data, 'availabilityActivityReport.csv');
      },
    );
  }

  async downloadWorkingInactivityReport() {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getWorkingInactivityReport',
      from,
      to,
      selectedCertificates || [],
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.displayCSV(data, 'workingActivityReport.csv');
      },
    );
  }

  async downloadAvailableUserJobPlanningReport() {
    const { selectedDepartmentIds, selectedCertificates, selectedPoolerType } =
      await this.getFiltersFromQuery();
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getAvailableUserJobPlanningReport',
      selectedCertificates || [],
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.displayCSV(data, 'availableUserPlanningReport.csv');
      },
    );
  }

  async downloadPoolerAssignmentReport(perPooler?: boolean) {
    const { selectedDepartmentIds, selectedCertificates, selectedPoolerType } =
      await this.getFiltersFromQuery();
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getPoolerAssignmentReport',
      selectedCertificates || [],
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      perPooler,
      (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.displayCSV(data, 'poolerOverviewReport.csv');
      },
    );
  }

  async downloadDisponentAssignmentReport() {
    const { selectedDepartmentIds } = await this.getFiltersFromQuery();
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getDisponentAssignmentReport',
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.displayCSV(data, 'disponentOverviewReport.csv');
      },
    );
  }

  async downloadRequesterAssignmentReport() {
    const { selectedDepartmentIds } = await this.getFiltersFromQuery();
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getRequesterAssignmentReport',
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      (error: Meteor.Error, data: any) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.displayCSV(data, 'requesterOverviewReport.csv');
      },
    );
  }

  displayCSV(data: string, fileName: string) {
    this.ngZone.run(() => {
      this.isLoading = false;
    });
    let blob = new Blob(['\ufeff' + he.decode(data)], {
      type: 'text/csv',
    });
    let url = URL.createObjectURL(blob);
    let a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.dispatchEvent(
      new MouseEvent(`click`, {
        bubbles: false,
        cancelable: true,
        view: window,
      }),
    );
  }

  async downloadCharts(): Promise<void> {
    const { from, to, selectedDepartmentIds, selectedCertificates } =
      await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getArchivedJobsChartsExcelBuffer',
      from,
      to,
      selectedCertificates || [],
      this.i18n,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      async (error: Meteor.Error, buffer: ArrayBuffer) => {
        if (!from || !to) {
          this.toastService.add({
            type: 'ERROR',
            message: 'noTimeSpanSelected',
          });
          return;
        }
        if (error) {
          console.error(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        this.ngZone.run(() => {
          this.isLoading = false;
        });
        const fileName = `${this.i18n?.archivedJobsReport}-${format(
          new Date(from),
          'd.M.y',
        )}-${format(new Date(to), 'd.M.y')}`;
        this.saveFile(
          fileName,
          '.xlsx',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          buffer,
        );
      },
    );
  }

  async downloadUserAvailabilityReport(
    type: 'EXCEL' | 'CSV' = 'EXCEL',
  ): Promise<void> {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getUserAvailabilityReport',
      from,
      to,
      type,
      selectedCertificates || [],
      this.i18n,
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      async (error: Meteor.Error, buffer: ArrayBuffer) => {
        if (!from || !to) {
          this.toastService.add({
            type: 'ERROR',
            message: 'noTimeSpanSelected',
          });
          return;
        }
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        const fileName = `${this.i18n.availabilitiesNoUmlaut}-${format(
          new Date(from),
          'd.M.y',
        )}-${format(addTimeZoneOffset(new Date(to)), 'd.M.y')}`;
        if (type === 'EXCEL') {
          this.saveFile(
            fileName,
            '.xlsx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            buffer,
          );
        } else {
          this.saveFile(fileName, '.csv', 'text/csv', buffer);
        }
      },
    );
  }

  saveFile(
    fileName: string,
    fileExtension: string,
    fileType: string,
    buffer: ArrayBuffer,
  ) {
    this.ngZone.run(() => {
      this.isLoading = false;
    });
    const blob = new Blob([buffer], { type: fileType });
    saveAs(blob, fileName + fileExtension);
  }

  async downloadPoolerReactionTimeReport() {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getPoolerReactionTimeReport',
      from,
      to,
      selectedCertificates || [],
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      (error: Meteor.Error, data: any) => {
        if (!from || !to) {
          this.toastService.add({
            type: 'ERROR',
            message: 'noTimeSpanSelected',
          });
          return;
        }
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        let fromDate = new Date(from);
        let toDate = new Date(to);

        let fileName =
          'reactionTimes-' +
          fromDate.getUTCDate() +
          '.' +
          (fromDate.getUTCMonth() + 1) +
          '.' +
          fromDate.getUTCFullYear() +
          '-' +
          toDate.getUTCDate() +
          '.' +
          (toDate.getUTCMonth() + 1) +
          '.' +
          toDate.getUTCFullYear() +
          '.csv';
        this.displayCSV(data, fileName);
      },
    );
  }

  async downloadTransfersReport() {
    const { from, to, selectedDepartmentIds } =
      await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getTransfersReport',
      from,
      to,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      (error: Meteor.Error, data: any) => {
        if (!from || !to) {
          this.toastService.add({
            type: 'ERROR',
            message: 'noTimeSpanSelected',
          });
          return;
        }
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        let fromDate = new Date(from);
        let toDate = new Date(to);

        let fileName =
          'transfers-' +
          fromDate.getUTCDate() +
          '.' +
          (fromDate.getUTCMonth() + 1) +
          '.' +
          fromDate.getUTCFullYear() +
          '-' +
          toDate.getUTCDate() +
          '.' +
          (toDate.getUTCMonth() + 1) +
          '.' +
          toDate.getUTCFullYear() +
          '.csv';
        this.displayCSV(data, fileName);
      },
    );
  }

  async downloadAvailabilityUpdatesReport() {
    const { from, to } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getAvailabilityUpdatesReport',
      from,
      to,
      this.i18n,
      async (error: Meteor.Error, buffer: ArrayBuffer) => {
        if (!from || !to) {
          this.toastService.add({
            type: 'ERROR',
            message: 'noTimeSpanSelected',
          });
          return;
        }
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }

        const fileName = `availability-updates-${format(
          new Date(from),
          'd.M.y',
        )}-${format(addTimeZoneOffset(new Date(to)), 'd.M.y')}`;

        this.saveFile(
          fileName,
          '.xlsx',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          buffer,
        );
      },
    );
  }

  async downloadPlanningMatrixReport() {
    const {
      from,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
    } = await this.getFiltersFromQuery();
    if (!from) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getPlanningMatrixReport',
      from,
      selectedCertificates || [],
      this.i18n,
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      async (error: Meteor.Error, buffer: ArrayBuffer) => {
        if (!from) {
          this.toastService.add({
            type: 'ERROR',
            message: 'noTimeSpanSelected',
          });
          return;
        }
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }

        const fileName = `planning-martrix-${format(new Date(from), 'd.M.y')}`;

        this.saveFile(
          fileName,
          '.xlsx',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          buffer,
        );
      },
    );
  }

  async downloadHoursByDepartmentReport() {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
      selectedCustomerIds,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getHoursByDepartmentReport',
      from,
      to,
      this.i18n,
      selectedCertificates || [],
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      selectedCustomerIds.length > 0 ? selectedCustomerIds : null,
      async (error: Meteor.Error, buffer: ArrayBuffer) => {
        if (!from || !to) {
          this.toastService.add({
            type: 'ERROR',
            message: 'noTimeSpanSelected',
          });
          return;
        }
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }

        const fileName = `hours-by-department-${format(
          new Date(from),
          'd.M.y',
        )}-${format(addTimeZoneOffset(new Date(to)), 'd.M.y')}`;

        this.saveFile(
          fileName,
          '.xlsx',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          buffer,
        );
      },
    );
  }

  downloadCustomerConnectionsReport() {
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getCustomerConnectionsReport',
      this.i18n,
      async (error: Meteor.Error, buffer: ArrayBuffer) => {
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }

        const fileName = `customer-connections-${format(new Date(), 'd.M.y')}`;

        this.saveFile(
          fileName,
          '.xlsx',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          buffer,
        );
      },
    );
  }

  getTimespanFilenameString(from?: string, to?: string): string {
    if (!from || !to) {
      return '';
    }
    return `${format(new Date(from), 'd.M.y')}-${format(
      new Date(to),
      'd.M.y',
    )}`;
  }

  async downloadSmsUsageReport() {
    const { from, to } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getSmsUsageReport',
      from,
      to,
      (error: Meteor.Error, data: any) => {
        if (!from || !to) {
          this.toastService.add({
            type: 'ERROR',
            message: 'noTimeSpanSelected',
          });
          return;
        }
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        let fromDate = new Date(from);
        let toDate = new Date(to);
        let fileName =
          'smsUsage-' +
          fromDate.getUTCDate() +
          '.' +
          (fromDate.getUTCMonth() + 1) +
          '.' +
          fromDate.getUTCFullYear() +
          '-' +
          toDate.getUTCDate() +
          '.' +
          (toDate.getUTCMonth() + 1) +
          '.' +
          toDate.getUTCFullYear() +
          '.csv';
        this.displayCSV(data, fileName);
      },
    );
  }

  async downloadExternalCancellationsReport(type: 'EXCEL' | 'CSV' = 'EXCEL') {
    const {
      from,
      to,
      selectedDepartmentIds,
      selectedCertificates,
      selectedPoolerType,
      selectedCustomerIds,
    } = await this.getFiltersFromQuery();
    if (!from || !to) {
      this.toastService.add({ type: 'ERROR', message: 'noTimeSpanSelected' });
      return;
    }
    this.ngZone.run(() => {
      this.isLoading = true;
    });
    Meteor.call(
      'getExternalCancellationsReport',
      from,
      to,
      type,
      selectedCertificates || [],
      this.i18n,
      selectedPoolerType || null,
      selectedDepartmentIds.length > 0 ? selectedDepartmentIds : null,
      selectedCustomerIds.length > 0 ? selectedCustomerIds : null,
      (error: Meteor.Error, buffer: ArrayBuffer) => {
        if (!from || !to) {
          this.toastService.add({
            type: 'ERROR',
            message: 'noTimeSpanSelected',
          });
          return;
        }
        if (error) {
          console.log(error);
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        const fileName = `externe-absagen-${format(
          new Date(from),
          'd.M.y',
        )}-${format(addTimeZoneOffset(new Date(to)), 'd.M.y')}`;
        if (type === 'EXCEL') {
          this.saveFile(
            fileName,
            '.xlsx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            buffer,
          );
        } else {
          this.saveFile(fileName, '.csv', 'text/csv', buffer);
        }
      },
    );
  }
}
